/**
 * How a site and a cluster of sites look on the map.
 *
 * Marker colour is the map's whole legend: a site paints its status, and an
 * alarming one paints its worst alarm's severity. A cluster paints the statuses
 * beneath it as a conic-gradient ring so a collapsed group still reports how
 * much of it is in trouble.
 *
 * Contains:
 * - siteMarkerColor — one site's colour
 * - createSiteIcon / createSelectedSiteIcon — analog/digital marker, plain or selected
 * - createBreakdownClusterIcon — the status-breakdown cluster badge
 */
import L from 'leaflet';

import { MAP_STATUS_COLORS } from '@sophocles/features/dashboards/chipStyles';
import { createCircleIcon, createDigitalIcon } from '@sophocles/features/dashboards/utils/mapMarkerIcons';
import { clusterBreakdown, deriveSiteStatus, SITE_STATUS } from '@sophocles/features/map/mapState';
import { getSeverityColor } from '@sophocles/utils/alarmSeverity';

const STATUS_MARKER_COLOR = Object.freeze({
  [SITE_STATUS.ALARMING]: MAP_STATUS_COLORS.alarm,
  [SITE_STATUS.SILENT]: MAP_STATUS_COLORS.silent,
  [SITE_STATUS.OFFLINE]: MAP_STATUS_COLORS.offline,
  [SITE_STATUS.ONLINE]: MAP_STATUS_COLORS.ok,
});

// Ring segments are painted worst-first so the most urgent status leads.
const RING_ORDER = Object.freeze([
  [SITE_STATUS.ALARMING, MAP_STATUS_COLORS.alarm],
  [SITE_STATUS.OFFLINE, MAP_STATUS_COLORS.offline],
  [SITE_STATUS.SILENT, MAP_STATUS_COLORS.silent],
  [SITE_STATUS.ONLINE, MAP_STATUS_COLORS.ok],
]);

const CLUSTER_SIZE_SMALL = 34;
const CLUSTER_SIZE_MEDIUM = 40;
const CLUSTER_SIZE_LARGE = 46;
const CLUSTER_MEDIUM_THRESHOLD = 100;
const CLUSTER_LARGE_THRESHOLD = 500;
const CLUSTER_RING_WIDTH = 12;
const DEGREES_PER_TURN = 360;

const SELECTED_MARKER_SIZE = 26;
const SELECTED_HALO_INSET = 6;

/**
 * The colour one site's marker paints. An alarming site paints its worst
 * alarm's severity so the map distinguishes a critical site from a merely noisy
 * one; every other site paints its status.
 *
 * @param {object} site - a map site row.
 * @returns {string} a CSS colour.
 */
export function siteMarkerColor(site) {
  const status = deriveSiteStatus(site);
  if (status === SITE_STATUS.ALARMING) {
    return site.max_severity ? getSeverityColor(site.max_severity) : MAP_STATUS_COLORS.alarm;
  }
  return STATUS_MARKER_COLOR[status];
}

/**
 * The marker for one site: a disc for an analog site, a squared-off tile for a
 * digital one.
 *
 * @param {object} site
 * @param {string} color
 * @returns {L.DivIcon}
 */
export function createSiteIcon(site, color) {
  return site.is_digital ? createDigitalIcon(color) : createCircleIcon(color);
}

/**
 * The selected site's marker — the same dot inside a matching halo, so the
 * sidebar selection is findable on a dense map without changing the site's
 * colour vocabulary.
 *
 * @param {object} site
 * @param {string} color
 * @returns {L.DivIcon}
 */
export function createSelectedSiteIcon(site, color) {
  const inner = SELECTED_MARKER_SIZE - SELECTED_HALO_INSET * 2;
  const radius = site.is_digital ? '4px' : '50%';
  return L.divIcon({
    className: 'site-map-marker--selected',
    html: `<div style="width:${SELECTED_MARKER_SIZE}px;height:${SELECTED_MARKER_SIZE}px;border-radius:50%;background:${color}33;border:2px solid ${color};box-sizing:border-box;display:flex;align-items:center;justify-content:center">
      <div style="width:${inner}px;height:${inner}px;border-radius:${radius};background:${color};border:2px solid white;box-sizing:border-box"></div>
    </div>`,
    iconSize: [SELECTED_MARKER_SIZE, SELECTED_MARKER_SIZE],
    iconAnchor: [SELECTED_MARKER_SIZE / 2, SELECTED_MARKER_SIZE / 2],
  });
}

function clusterSize(total) {
  if (total < CLUSTER_MEDIUM_THRESHOLD) return CLUSTER_SIZE_SMALL;
  if (total < CLUSTER_LARGE_THRESHOLD) return CLUSTER_SIZE_MEDIUM;
  return CLUSTER_SIZE_LARGE;
}

/**
 * Conic-gradient badge: a colored ring split by the cluster's status breakdown
 * with the count on a solid disc so it stays readable over any ring.
 *
 * @param {object} cluster - a leaflet.markercluster cluster; child markers carry `_status`.
 * @returns {L.DivIcon}
 */
export function createBreakdownClusterIcon(cluster) {
  const statuses = cluster.getAllChildMarkers().map(marker => marker.options._status);
  const total = statuses.length;
  const breakdown = clusterBreakdown(statuses);

  let accumulated = 0;
  const stops = RING_ORDER.filter(([status]) => breakdown[status] > 0)
    .map(([status, color]) => {
      const start = (accumulated / total) * DEGREES_PER_TURN;
      accumulated += breakdown[status];
      const end = (accumulated / total) * DEGREES_PER_TURN;
      return `${color} ${start}deg ${end}deg`;
    })
    .join(', ');

  const size = clusterSize(total);
  const inner = size - CLUSTER_RING_WIDTH;
  return L.divIcon({
    className: 'site-group-cluster-icon',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:conic-gradient(${stops});display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,0.3)">
      <span style="width:${inner}px;height:${inner}px;border-radius:50%;background:var(--surface-primary);color:var(--text-primary, #222);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700">${total}</span>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}
